import { stripMarkdownFences } from "../../utils";

type JsonRecord = Record<string, unknown>;

export interface OpenAIResponseLike {
  output?: unknown;
  output_text?: unknown;
}

export function parseJsonText<T = JsonRecord>(rawText: string): T {
  return JSON.parse(stripMarkdownFences(rawText)) as T;
}

export function extractOutputJsonPayload<T = JsonRecord>(data: OpenAIResponseLike): T | null {
  const outputItems = Array.isArray(data.output) ? data.output : [];
  for (const item of outputItems) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const outputItem = item as Record<string, unknown>;
    if (outputItem.type !== "message" || !Array.isArray(outputItem.content)) {
      continue;
    }
    for (const content of outputItem.content) {
      if (!content || typeof content !== "object") {
        continue;
      }
      const contentItem = content as Record<string, unknown>;
      if (contentItem.type === "output_json" && contentItem.json && typeof contentItem.json === "object") {
        return contentItem.json as T;
      }
    }
  }
  return null;
}

export function extractStructuredPayload<T = JsonRecord>(data: OpenAIResponseLike): T | null {
  const outputItems = Array.isArray(data.output) ? data.output : [];
  for (const item of outputItems) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const outputItem = item as Record<string, unknown>;
    if (outputItem.type !== "message" || !Array.isArray(outputItem.content)) {
      continue;
    }
    for (const content of outputItem.content) {
      if (!content || typeof content !== "object") {
        continue;
      }
      const contentItem = content as Record<string, unknown>;
      if (contentItem.type === "output_json" && contentItem.json && typeof contentItem.json === "object") {
        return contentItem.json as T;
      }
      if (contentItem.type === "output_text" && typeof contentItem.text === "string") {
        try {
          return parseJsonText<T>(contentItem.text);
        } catch {
          // Continue searching for structured payload in later content entries.
        }
      }
    }
  }

  if (typeof data.output_text === "string") {
    try {
      return parseJsonText<T>(data.output_text);
    } catch {
      return null;
    }
  }

  return null;
}

export function extractMessageTextContent(data: OpenAIResponseLike): string {
  let contentText = "";
  const outputItems = Array.isArray(data.output) ? data.output : [];
  for (const item of outputItems) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const outputItem = item as Record<string, unknown>;
    if (outputItem.type !== "message" || !Array.isArray(outputItem.content)) {
      continue;
    }
    for (const content of outputItem.content) {
      if (!content || typeof content !== "object") {
        continue;
      }
      const contentItem = content as Record<string, unknown>;
      if (contentItem.type === "output_text" && typeof contentItem.text === "string") {
        contentText = contentItem.text;
      }
    }
  }
  if (!contentText && typeof data.output_text === "string") {
    contentText = data.output_text;
  }
  return contentText;
}
